import { useEffect, useMemo, useState } from 'react';
import { NavLink, Outlet, useNavigate, useLocation } from 'react-router';
import { Check, ChevronDown, Home, ListChecks, MessageSquare, Plus, User } from 'lucide-react';
import { useClientBusiness, rememberBusiness, ALL } from '../lib/business';
import { UploadIndicator } from '../uploads/UploadIndicator';
import { loadPaused } from '../uploads/store';
import { useClientSettings } from '../lib/settings';
import { NotificationBell } from './NotificationBell';
import { ThemeIconButton } from './ThemeToggle';
import { Sheet } from '../ui/Sheet';
import { Avatar, BizDot, setBusinessOrder } from '../ui/misc';
import { cn } from '../lib/cn';
import { Logo } from './Logo';
import { ImpersonationBanner } from './ImpersonationBanner';
import { impersonationOf } from '../lib/impersonation';

/** Client app frame: S1-style top bar on desktop, bottom tab bar on phones (§11b, §12). */
export function ClientShell() {
  const { me, slug, business, businesses, isAll, valid } = useClientBusiness();
  const nav = useNavigate();
  const base = `/b/${slug}`;
  useClientSettings();
  const names = useMemo(() => {
    setBusinessOrder(businesses.map((b) => b.id));
    return Object.fromEntries(businesses.map((b) => [b.id, b.name]));
  }, [businesses]);

  useEffect(() => {
    if (valid) rememberBusiness(me.id, slug);
  }, [me.id, slug, valid]);

  // Paused uploads from before a reload, for every business of this user (§6). Not while acting.
  const acting = !!impersonationOf(me);
  useEffect(() => {
    if (acting) return;
    for (const b of businesses) void loadPaused({ userId: me.id, businessId: b.id });
  }, [me.id, businesses, acting]);

  useEffect(() => {
    if (!valid) nav(businesses.length ? `/b/${businesses.length > 1 ? ALL : businesses[0]!.slug}` : '/no-access', { replace: true });
  }, [valid]); // eslint-disable-line react-hooks/exhaustive-deps

  const tabs = [
    { to: base, label: 'Home', Icon: Home, end: true },
    { to: `${base}/requests`, label: 'Requests', Icon: ListChecks },
    { to: `${base}/new`, label: 'New', Icon: Plus, primary: true },
    { to: `${base}/messages`, label: 'Messages', Icon: MessageSquare },
    { to: '/me', label: 'Me', Icon: User },
  ];

  if (!valid) return null;
  return (
    <div className="flex min-h-dvh flex-col">
      <a href="#main" className="sr-only z-50 rounded-lg bg-card px-3 py-2 focus:not-sr-only focus:fixed focus:top-2 focus:left-2">
        Skip to content
      </a>
      <header className={cn('sticky top-0 z-30 border-b border-border bg-card', !acting && 'pt-[env(safe-area-inset-top)]')}>
        {acting && <ImpersonationBanner className="pt-[env(safe-area-inset-top)] box-content" />}
        <div className="mx-auto flex h-14 max-w-[1312px] items-center gap-2 px-3 sm:px-6 lg:h-16 lg:gap-4 lg:px-8">
          <Logo className="hidden lg:flex" />
          <BusinessSwitcher current={business?.slug ?? ALL} businesses={businesses} isAll={isAll} />
          <nav aria-label="Main" className="ml-2 hidden items-center gap-1 lg:flex">
            {[tabs[0]!, tabs[1]!, tabs[3]!].map((t) => (
              <NavLink
                key={t.to}
                to={t.to}
                end={t.end}
                className={({ isActive }) =>
                  cn('inline-flex h-11 items-center rounded-lg px-3 text-[15px]', isActive ? 'bg-primary-soft font-medium text-primary-text' : 'text-ink-2 hover:bg-surface')
                }
              >
                {t.label === 'Home' ? 'Overview' : t.label === 'Requests' ? 'Work orders' : t.label}
              </NavLink>
            ))}
          </nav>
          <div className="ml-auto flex items-center gap-1">
            <UploadIndicator businessNames={names} />
            <NotificationBell />
            <ThemeIconButton />
            <NavLink
              to={`${base}/new`}
              className="ml-2 hidden h-10 items-center gap-2 rounded-lg bg-primary px-4 text-[15px] font-semibold text-primary-ink hover:brightness-110 lg:inline-flex"
            >
              <Plus aria-hidden className="size-4" /> New work order
            </NavLink>
            <NavLink to="/me" aria-label="Me: profile and settings" className="ml-1 hidden rounded-full lg:inline-flex">
              <Avatar name={me.name} size={40} />
            </NavLink>
          </div>
        </div>
      </header>

      <main id="main" tabIndex={-1} className="mx-auto w-full max-w-[1312px] flex-1 px-3 pt-4 pb-[calc(5.5rem+env(safe-area-inset-bottom))] outline-none sm:px-6 lg:px-8 lg:pt-8 lg:pb-12">
        <Outlet />
      </main>

      <BottomTabs tabs={tabs} label="Main" />
    </div>
  );
}

export function BottomTabs({
  tabs,
  label,
}: {
  tabs: { to: string; label: string; Icon: typeof Home; end?: boolean; primary?: boolean; badge?: number }[];
  label: string;
}) {
  return (
    <nav
      aria-label={label}
      className="fixed inset-x-0 bottom-0 z-30 border-t border-border bg-card pb-[env(safe-area-inset-bottom)] in-data-[kb=open]:hidden lg:hidden"
    >
      <ul className="mx-auto flex max-w-xl">
        {tabs.map(({ to, label: l, Icon, end, primary, badge }) => (
          <li key={to} className="flex-1">
            <NavLink
              to={to}
              end={end}
              className={({ isActive }) =>
                cn('flex h-16 flex-col items-center justify-center gap-0.5 text-[11px] font-medium', isActive ? 'text-primary-text' : 'text-muted')
              }
            >
              {({ isActive }) => (
                <>
                  <span
                    className={cn(
                      'relative inline-flex items-center justify-center',
                      primary ? 'size-9 rounded-full bg-primary text-primary-ink' : 'h-7 w-12 rounded-full',
                      !primary && isActive && 'bg-primary-soft',
                    )}
                  >
                    <Icon aria-hidden className={primary ? 'size-5' : 'size-[22px]'} strokeWidth={isActive || primary ? 2.25 : 1.75} />
                    {badge ? (
                      <span aria-hidden className="absolute -top-0.5 right-1 min-w-4 rounded-full bg-danger px-1 text-[10px] leading-4 text-white">
                        {badge > 99 ? '99+' : badge}
                      </span>
                    ) : null}
                  </span>
                  <span>
                    {l}
                    {badge ? <span className="sr-only">, {badge} new</span> : null}
                  </span>
                </>
              )}
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}

function BusinessSwitcher({ current, businesses, isAll }: { current: string; businesses: { id: string; name: string; slug: string }[]; isAll: boolean }) {
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  const loc = useLocation();
  const cur = businesses.find((b) => b.slug === current);
  if (businesses.length <= 1) {
    return <span className="truncate px-1 text-[15px] font-semibold text-ink lg:hidden">{cur?.name ?? 'Workdesk'}</span>;
  }
  const go = (slug: string) => {
    setOpen(false);
    // Keep the section (requests/messages/new) but drop ids that belong to the old business.
    const section = loc.pathname.split('/')[3];
    const keep = section && ['requests', 'messages', 'new'].includes(section) ? `/${section}` : '';
    nav(`/b/${slug}${keep}`);
  };
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
        className="inline-flex h-11 min-w-0 items-center gap-2 rounded-lg border border-border bg-card px-3 text-[15px] font-medium text-ink hover:bg-surface-alt"
      >
        {cur ? <BizDot id={cur.id} /> : <span aria-hidden className="size-2 rounded-full bg-primary" />}
        <span className="truncate">{isAll ? `All businesses · ${businesses.length}` : cur?.name}</span>
        <ChevronDown aria-hidden className="size-4 shrink-0 text-muted" />
        <span className="sr-only">, change business</span>
      </button>
      <Sheet open={open} onClose={() => setOpen(false)} title="Switch business" side="bottom">
        <ul className="-mx-2 pb-2">
          {[{ id: 'all', name: 'All businesses', slug: ALL }, ...businesses].map((b) => {
            const active = b.slug === current;
            return (
              <li key={b.slug}>
                <button
                  type="button"
                  onClick={() => go(b.slug)}
                  aria-current={active ? 'true' : undefined}
                  className={cn('flex h-12 w-full items-center gap-3 rounded-lg px-3 text-left text-[15px]', active ? 'bg-primary-soft font-semibold text-primary-text' : 'text-ink hover:bg-surface')}
                >
                  {b.slug === ALL ? <span aria-hidden className="size-2 rounded-full bg-primary" /> : <BizDot id={b.id} />}
                  <span className="flex-1 truncate">{b.name}</span>
                  {active && <Check aria-hidden className="size-4" />}
                </button>
              </li>
            );
          })}
        </ul>
      </Sheet>
    </>
  );
}
